/**
 * PaywallAnimations Component
 *
 * Collection of 5 premium paywall animation styles:
 * 1. PeekABoo - Text types out then blurs
 * 2. BreathingBlur - Pulsing blur depth
 * 3. ScrollingTicker - Horizontal stock ticker behind blur
 * 4. CountingUp - Numbers animate up, locked ones glitch to "??"
 * 5. ShimmerWave - Premium diagonal shimmer sweep
 */

import { FC, useState, useEffect } from 'react';
import { css, keyframes } from '@emotion/react';
import { colors, spacing, fontStyles } from '../../styles';
import * as stores from '../../stores';

// ============================================
// ANIMATION 1: PEEK-A-BOO (Type → Blur Cycle)
// ============================================

const typewriterKeyframes = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const blurInKeyframes = keyframes`
  0% { filter: blur(0px); opacity: 1; }
  100% { filter: blur(8px); opacity: 0.6; }
`;

interface PeekABooBullet {
  emoji: string;
  text: string;
}

interface PeekABooPaywallProps {
  bullets?: PeekABooBullet[];
  onUnlock?: () => void;
}

export const PeekABooPaywall: FC<PeekABooPaywallProps> = ({
  bullets = [
    { emoji: '🟢', text: 'Tech stocks rally as NVIDIA beats expectations...' },
    { emoji: '🔴', text: 'Fed comments spark volatility concerns...' },
    { emoji: '⚪', text: 'Oil prices stabilize near $78 per barrel...' },
  ],
  onUnlock,
}) => {
  const [activeBullet, setActiveBullet] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'showing' | 'blurring'>('typing');

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase((prev) => {
        if (prev === 'typing') return 'showing';
        if (prev === 'showing') return 'blurring';
        setActiveBullet((b) => (b + 1) % bullets.length);
        return 'typing';
      });
    }, 1500);
    return () => clearInterval(interval);
  }, [bullets.length]);

  return (
    <div
      css={css`
        background: ${colors.snow};
        border: 0.5px solid ${colors.cloud};
        border-radius: 16px;
        padding: ${spacing.regular};
        position: relative;
        overflow: hidden;

        html[data-theme='dark'] & {
          background: #242424;
          border-color: rgba(255, 255, 255, 0.06);
        }
      `}
    >
      <h3
        css={css`
          ${fontStyles.dog};
          font-weight: 500;
          color: var(--text-color);
          margin: 0 0 ${spacing.centi} 0;
        `}
      >
        Today&apos;s Market Update
      </h3>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.centi};
          min-height: 120px;
        `}
      >
        {bullets.map((bullet, index) => (
          <div
            key={index}
            css={css`
              display: flex;
              align-items: flex-start;
              gap: ${spacing.milli};
              ${fontStyles.thinMouse};
              color: var(--text-color);
            `}
          >
            <span
              css={css`
                flex-shrink: 0;
                font-size: 12px;
              `}
            >
              {bullet.emoji}
            </span>
            <span
              css={css`
                ${index === activeBullet && phase === 'typing'
                  ? css`
                      overflow: hidden;
                      white-space: nowrap;
                      animation: ${typewriterKeyframes} 1.2s steps(40, end);
                    `
                  : ''}
                ${index === activeBullet && phase === 'blurring'
                  ? css`
                      animation: ${blurInKeyframes} 0.5s ease forwards;
                    `
                  : ''}
                ${index !== activeBullet
                  ? css`
                      filter: blur(6px);
                      opacity: 0.5;
                    `
                  : ''}
              `}
            >
              {bullet.text}
            </span>
          </div>
        ))}
      </div>

      {/* Unlock button */}
      <div
        css={css`
          display: flex;
          justify-content: center;
          margin-top: ${spacing.deci};
        `}
      >
        <button
          onClick={onUnlock || (() => stores.showPaymentModalByPreference('market_summary_peek'))}
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.milli};
            background: ${colors.sugar};
            border: 1.5px solid ${colors.petrolBlue}50;
            padding: ${spacing.centi} ${spacing.deci};
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.2s;
            ${fontStyles.flea}
            color: ${colors.petrolBlue};
            font-weight: 500;

            &:hover {
              background: ${colors.fog};
              border-color: ${colors.petrolBlue};
            }

            html[data-theme='dark'] & {
              background: ${colors.liquorice};
              color: ${colors.skyBlue};
              border-color: ${colors.skyBlue}50;

              &:hover {
                border-color: ${colors.skyBlue};
              }
            }
          `}
        >
          <span>🔒</span>
          <span>Unlock Full Insights</span>
        </button>
      </div>
    </div>
  );
};

// ============================================
// ANIMATION 2: BREATHING BLUR
// ============================================

const breatheKeyframes = keyframes`
  0%, 100% { filter: blur(4px); opacity: 0.7; }
  50% { filter: blur(8px); opacity: 0.4; }
`;

const floatKeyframes = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

interface BreathingBlurPaywallProps {
  children?: React.ReactNode;
  onUnlock?: () => void;
}

export const BreathingBlurPaywall: FC<BreathingBlurPaywallProps> = ({ children, onUnlock }) => {
  return (
    <div
      css={css`
        position: relative;
        border-radius: 16px;
        overflow: hidden;
      `}
    >
      {/* Blurred content layer */}
      <div
        css={css`
          animation: ${breatheKeyframes} 3s ease-in-out infinite;
          pointer-events: none;
        `}
      >
        {children || (
          <div
            css={css`
              background: linear-gradient(135deg, ${colors.leaf}40, ${colors.berry}40, ${colors.tungsten}40);
              height: 200px;
              border-radius: 8px;
            `}
          />
        )}
      </div>

      {/* Floating unlock overlay */}
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        `}
      >
        <button
          onClick={onUnlock || (() => stores.showPaymentModalByPreference('market_pulse_breathing'))}
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: ${spacing.micro};
            background: ${colors.sugar};
            border: 1.5px solid ${colors.petrolBlue}50;
            padding: ${spacing.centi} ${spacing.deci};
            border-radius: 12px;
            cursor: pointer;
            animation: ${floatKeyframes} 2.5s ease-in-out infinite;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

            html[data-theme='dark'] & {
              background: ${colors.liquorice};
            }
          `}
        >
          <span
            css={css`
              font-size: 18px;
            `}
          >
            🔒
          </span>
          <span
            css={css`
              ${fontStyles.flea}
              color: ${colors.petrolBlue};
              font-weight: 500;
              white-space: nowrap;
              text-align: center;

              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            `}
          >
            Unlock
            <br />
            with Pro
          </span>
        </button>
      </div>
    </div>
  );
};

// ============================================
// ANIMATION 3: SCROLLING TICKER
// ============================================

const scrollTickerKeyframes = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const glowKeyframes = keyframes`
  0%, 100% { box-shadow: 0 0 8px ${colors.petrolBlue}40; }
  50% { box-shadow: 0 0 16px ${colors.petrolBlue}80; }
`;

interface ScrollingTickerPaywallProps {
  tickers?: { symbol: string; change: string; positive: boolean }[];
  onUnlock?: () => void;
}

export const ScrollingTickerPaywall: FC<ScrollingTickerPaywallProps> = ({
  tickers = [
    { symbol: 'AAPL', change: '+2.1%', positive: true },
    { symbol: 'GOOGL', change: '-0.8%', positive: false },
    { symbol: 'TSLA', change: '+4.3%', positive: true },
    { symbol: 'MSFT', change: '+1.2%', positive: true },
    { symbol: 'AMZN', change: '-1.5%', positive: false },
    { symbol: 'NVDA', change: '+3.7%', positive: true },
  ],
  onUnlock,
}) => {
  // Duplicate tickers for seamless loop
  const allTickers = [...tickers, ...tickers];

  return (
    <div
      css={css`
        background: ${colors.snow};
        border: 0.5px solid ${colors.cloud};
        border-radius: 16px;
        padding: ${spacing.regular};
        position: relative;
        overflow: hidden;

        html[data-theme='dark'] & {
          background: #242424;
          border-color: rgba(255, 255, 255, 0.06);
        }
      `}
    >
      <h3
        css={css`
          ${fontStyles.dog};
          font-weight: 500;
          color: var(--text-color);
          margin: 0 0 ${spacing.centi} 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
        `}
      >
        <span>Market Summary</span>
        <span
          css={css`
            ${fontStyles.flea}
            background: ${colors.petrolBlue};
            color: white;
            padding: 2px 8px;
            border-radius: 4px;
          `}
        >
          PRO
        </span>
      </h3>

      {/* Scrolling ticker */}
      <div
        css={css`
          overflow: hidden;
          margin-bottom: ${spacing.deci};
          border-radius: 8px;
          background: ${colors.fog};
          padding: ${spacing.centi} 0;

          html[data-theme='dark'] & {
            background: ${colors.liquorice};
          }
        `}
      >
        <div
          css={css`
            display: flex;
            gap: ${spacing.deci};
            animation: ${scrollTickerKeyframes} 15s linear infinite;
            width: fit-content;
          `}
        >
          {allTickers.map((ticker, i) => (
            <span
              key={i}
              css={css`
                ${fontStyles.mouse}
                white-space: nowrap;
                color: ${ticker.positive ? colors.leaf : colors.berry};
                filter: blur(2px);
              `}
            >
              {ticker.symbol} {ticker.change}
            </span>
          ))}
        </div>
      </div>

      {/* Frosted glass content area */}
      <div
        css={css`
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(8px);
          border-radius: 12px;
          padding: ${spacing.deci};
          display: flex;
          flex-direction: column;
          gap: ${spacing.centi};
          min-height: 100px;
          align-items: center;
          justify-content: center;

          html[data-theme='dark'] & {
            background: rgba(0, 0, 0, 0.5);
          }
        `}
      >
        {/* Placeholder blurred lines */}
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            css={css`
              width: ${100 - i * 15}%;
              height: 14px;
              background: ${colors.cloud};
              border-radius: 4px;
              opacity: 0.5;

              html[data-theme='dark'] & {
                background: ${colors.tungsten};
              }
            `}
          />
        ))}

        <button
          onClick={onUnlock || (() => stores.showPaymentModalByPreference('market_summary_ticker'))}
          css={css`
            margin-top: ${spacing.centi};
            display: flex;
            align-items: center;
            gap: ${spacing.milli};
            background: ${colors.petrolBlue};
            border: none;
            padding: ${spacing.centi} ${spacing.deci};
            border-radius: 8px;
            cursor: pointer;
            animation: ${glowKeyframes} 2s ease-in-out infinite;
            ${fontStyles.mouse}
            color: white;

            &:hover {
              background: ${colors.skyBlue};
            }
          `}
        >
          See Today&apos;s Insights
        </button>
      </div>
    </div>
  );
};

// ============================================
// ANIMATION 4: COUNTING UP
// ============================================

const glitchKeyframes = keyframes`
  0%, 90% { content: attr(data-value); }
  92% { content: '?'; }
  94% { content: attr(data-value); }
  96% { content: '??'; }
  98% { content: '?'; }
  100% { content: '??'; }
`;

interface CountingIndicator {
  label: string;
  value: number | string;
  locked?: boolean;
}

interface CountingUpPaywallProps {
  indicators?: CountingIndicator[];
  onUnlock?: () => void;
}

const CountingNumber: FC<{ value: number | string; locked?: boolean; duration?: number }> = ({
  value,
  locked,
  duration = 1500,
}) => {
  const [displayValue, setDisplayValue] = useState<string>(locked ? '0' : '0');
  const [isGlitching, setIsGlitching] = useState(false);

  useEffect(() => {
    if (typeof value !== 'number') {
      setDisplayValue(String(value));
      return;
    }

    const startTime = Date.now();
    const endValue = value;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * endValue);

      setDisplayValue(String(current));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else if (locked) {
        setIsGlitching(true);
      }
    };

    animate();
  }, [value, locked, duration]);

  if (isGlitching) {
    return (
      <span
        css={css`
          &::after {
            content: '??';
            animation: ${glitchKeyframes} 0.5s steps(1) forwards;
          }
        `}
      />
    );
  }

  return <span>{locked && isGlitching ? '??' : displayValue}</span>;
};

export const CountingUpPaywall: FC<CountingUpPaywallProps> = ({
  indicators = [
    { label: 'Fear/Greed', value: 72, locked: false },
    { label: 'Momentum', value: 65, locked: true },
    { label: 'S&P 500', value: '+1.2%', locked: false },
    { label: 'Sectors', value: 8, locked: true },
  ],
  onUnlock,
}) => {
  const [key, setKey] = useState(0);

  // Restart animation every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => setKey((k) => k + 1), 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      css={css`
        background: ${colors.snow};
        border: 0.5px solid ${colors.cloud};
        border-radius: 16px;
        padding: ${spacing.regular};

        html[data-theme='dark'] & {
          background: #242424;
          border-color: rgba(255, 255, 255, 0.06);
        }
      `}
    >
      <h3
        css={css`
          ${fontStyles.dog};
          font-weight: 500;
          color: var(--text-color);
          margin: 0 0 ${spacing.deci} 0;
        `}
      >
        Market Pulse
      </h3>

      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: ${spacing.centi};
        `}
      >
        {indicators.map((indicator, index) => (
          <div
            key={`${index}-${key}`}
            css={css`
              background: ${colors.fog};
              border-radius: 8px;
              padding: ${spacing.deci};
              text-align: center;

              html[data-theme='dark'] & {
                background: ${colors.liquorice};
              }
            `}
          >
            <div
              css={css`
                ${fontStyles.horse}
                color: ${indicator.locked ? colors.silver : colors.petrolBlue};
                font-weight: 600;
                margin-bottom: ${spacing.micro};

                html[data-theme='dark'] & {
                  color: ${indicator.locked ? colors.tungsten : colors.skyBlue};
                }
              `}
            >
              {typeof indicator.value === 'number' ? (
                <CountingNumber value={indicator.value} locked={indicator.locked} />
              ) : (
                indicator.value
              )}
              {indicator.locked && (
                <span
                  css={css`
                    opacity: 0;
                    animation: fadeIn 0.3s ease forwards;
                    animation-delay: 1.5s;

                    @keyframes fadeIn {
                      to {
                        opacity: 1;
                      }
                    }
                  `}
                >
                  ??
                </span>
              )}
            </div>
            <div
              css={css`
                ${fontStyles.flea}
                color: ${colors.silver};
              `}
            >
              {indicator.label}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={onUnlock || (() => stores.showPaymentModalByPreference('market_pulse_counting'))}
        css={css`
          width: 100%;
          margin-top: ${spacing.deci};
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${spacing.milli};
          background: ${colors.fog};
          border: 1.5px solid ${colors.petrolBlue}30;
          padding: ${spacing.centi} ${spacing.deci};
          border-radius: 8px;
          cursor: pointer;
          ${fontStyles.mouse}
          color: ${colors.petrolBlue};

          &:hover {
            background: ${colors.petrolBlue}10;
            border-color: ${colors.petrolBlue};
          }

          html[data-theme='dark'] & {
            background: ${colors.liquorice};
            color: ${colors.skyBlue};
            border-color: ${colors.skyBlue}30;

            &:hover {
              border-color: ${colors.skyBlue};
            }
          }
        `}
      >
        <span>🔒</span>
        <span>Get Full Picture • Unlock 2 more indicators</span>
      </button>
    </div>
  );
};

// ============================================
// ANIMATION 5: SHIMMER WAVE (Recommended)
// ============================================

const shimmerKeyframes = keyframes`
  0% { transform: translateX(-100%) rotate(-45deg); }
  100% { transform: translateX(200%) rotate(-45deg); }
`;

const sparkleKeyframes = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
`;

interface ShimmerWavePaywallProps {
  children?: React.ReactNode;
  title?: string;
  onUnlock?: () => void;
}

export const ShimmerWavePaywall: FC<ShimmerWavePaywallProps> = ({ children, title = 'Market Summary', onUnlock }) => {
  return (
    <div
      css={css`
        background: ${colors.snow};
        border: 0.5px solid ${colors.cloud};
        border-radius: 16px;
        padding: ${spacing.regular};
        position: relative;
        overflow: hidden;

        html[data-theme='dark'] & {
          background: #242424;
          border-color: rgba(255, 255, 255, 0.06);
        }
      `}
    >
      <h3
        css={css`
          ${fontStyles.dog};
          font-weight: 500;
          color: var(--text-color);
          margin: 0 0 ${spacing.centi} 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
        `}
      >
        <span>{title}</span>
        <span
          css={css`
            ${fontStyles.flea}
            background: linear-gradient(135deg, ${colors.petrolBlue}, ${colors.skyBlue});
            color: white;
            padding: 2px 8px;
            border-radius: 4px;
          `}
        >
          PRO
        </span>
      </h3>

      {/* Content area with shimmer */}
      <div
        css={css`
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          background: ${colors.fog};
          padding: ${spacing.deci};
          min-height: 140px;

          html[data-theme='dark'] & {
            background: ${colors.liquorice};
          }
        `}
      >
        {/* Blurred placeholder content */}
        <div
          css={css`
            filter: blur(6px);
            opacity: 0.6;
          `}
        >
          {children || (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${spacing.centi};
              `}
            >
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  css={css`
                    display: flex;
                    gap: ${spacing.milli};
                    align-items: center;
                  `}
                >
                  <div
                    css={css`
                      width: 16px;
                      height: 16px;
                      background: ${i % 2 === 0 ? colors.berry : colors.leaf};
                      border-radius: 50%;
                    `}
                  />
                  <div
                    css={css`
                      flex: 1;
                      height: 14px;
                      background: ${colors.cloud};
                      border-radius: 4px;
                      width: ${100 - i * 10}%;

                      html[data-theme='dark'] & {
                        background: ${colors.tungsten};
                      }
                    `}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Shimmer overlay */}
        <div
          css={css`
            position: absolute;
            top: -50%;
            left: 0;
            right: 0;
            bottom: -50%;
            background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%);
            width: 50%;
            animation: ${shimmerKeyframes} 3s ease-in-out infinite;
            pointer-events: none;

            html[data-theme='dark'] & {
              background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.15) 50%, transparent 100%);
            }
          `}
        />

        {/* Center unlock button */}
        <div
          css={css`
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          `}
        >
          <button
            onClick={onUnlock || (() => stores.showPaymentModalByPreference('market_summary_shimmer'))}
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.milli};
              background: ${colors.sugar};
              border: 1.5px solid ${colors.petrolBlue}50;
              padding: ${spacing.centi} ${spacing.deci};
              border-radius: 12px;
              cursor: pointer;
              box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
              ${fontStyles.mouse}
              color: ${colors.petrolBlue};
              font-weight: 500;
              transition: all 0.2s;

              &:hover {
                transform: scale(1.05);
                border-color: ${colors.petrolBlue};
              }

              html[data-theme='dark'] & {
                background: #1a1a1a;
                color: ${colors.skyBlue};
                border-color: ${colors.skyBlue}50;

                &:hover {
                  border-color: ${colors.skyBlue};
                }
              }
            `}
          >
            <span
              css={css`
                animation: ${sparkleKeyframes} 1.5s ease-in-out infinite;
              `}
            >
              ✨
            </span>
            <span>Unlock Pro</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// DEMO COMPONENT - Shows all 5 animations
// ============================================

export type PaywallAnimationType =
  | 'peek-a-boo'
  | 'breathing-blur'
  | 'scrolling-ticker'
  | 'counting-up'
  | 'shimmer-wave';

interface PaywallAnimationsDemoProps {
  onSelect?: (type: PaywallAnimationType) => void;
}

export const PaywallAnimationsDemo: FC<PaywallAnimationsDemoProps> = ({ onSelect }) => {
  const animations: { type: PaywallAnimationType; name: string; description: string }[] = [
    { type: 'peek-a-boo', name: '1. Peek-a-Boo', description: 'Text types out then blurs' },
    { type: 'breathing-blur', name: '2. Breathing Blur', description: 'Pulsing blur depth with floating lock' },
    {
      type: 'scrolling-ticker',
      name: '3. Scrolling Ticker',
      description: 'Horizontal stock ticker behind frosted glass',
    },
    { type: 'counting-up', name: '4. Counting Up', description: 'Numbers animate up, locked ones glitch to ??' },
    { type: 'shimmer-wave', name: '5. Shimmer Wave ⭐', description: 'Premium diagonal shimmer sweep' },
  ];

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.deci};
        max-width: 600px;
        margin: 0 auto;
        padding: ${spacing.deci};
      `}
    >
      <h2
        css={css`
          ${fontStyles.shark}
          color: var(--text-color);
          margin: 0 0 ${spacing.centi} 0;
        `}
      >
        Paywall Animation Options
      </h2>
      <p
        css={css`
          ${fontStyles.cat}
          color: ${colors.silver};
          margin: 0 0 ${spacing.deci} 0;
        `}
      >
        Click any animation to select it for your paywalled content
      </p>

      {animations.map(({ type, name, description }) => (
        <div
          key={type}
          onClick={() => onSelect?.(type)}
          css={css`
            cursor: pointer;
            transition: transform 0.2s;

            &:hover {
              transform: scale(1.01);
            }
          `}
        >
          <div
            css={css`
              ${fontStyles.fatCat}
              color: var(--text-color);
              margin-bottom: ${spacing.milli};
            `}
          >
            {name}
          </div>
          <div
            css={css`
              ${fontStyles.thinFlea}
              color: ${colors.silver};
              margin-bottom: ${spacing.centi};
            `}
          >
            {description}
          </div>

          {type === 'peek-a-boo' && <PeekABooPaywall />}
          {type === 'breathing-blur' && (
            <BreathingBlurPaywall>
              <div
                css={css`
                  display: grid;
                  grid-template-columns: repeat(4, 1fr);
                  gap: 4px;
                  height: 150px;
                `}
              >
                {Array.from({ length: 16 }).map((_, i) => (
                  <div
                    key={i}
                    css={css`
                      background: ${i % 3 === 0
                          ? colors.leaf
                          : i % 3 === 1
                            ? colors.berry
                            : colors.tungsten}${Math.random() > 0.5 ? '80' : 'cc'};
                      border-radius: 4px;
                    `}
                  />
                ))}
              </div>
            </BreathingBlurPaywall>
          )}
          {type === 'scrolling-ticker' && <ScrollingTickerPaywall />}
          {type === 'counting-up' && <CountingUpPaywall />}
          {type === 'shimmer-wave' && <ShimmerWavePaywall />}
        </div>
      ))}
    </div>
  );
};

export default PaywallAnimationsDemo;

// Re-export TypewriterShowcase for use in feature sections
export { TypewriterShowcase } from './TypewriterShowcase';
export type { TypewriterBullet } from './TypewriterShowcase';
